// DashboardAdmin.tsx
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Award,
  X,
  UserCheck,
  Users,
  BookOpen,
  DollarSign,
  Activity,
  Download,
  Loader,
  Shield,
  RefreshCw,
  GraduationCap,
  UserPlus,
  TrendingUp,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Database,
  Calendar,
  BarChart3,
  PieChart as PieChartIcon,
} from "lucide-react";
import { adminApi, analyticsApi, showError, gamificationApi } from "../../lib";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  AreaChart,
  Area,
} from "recharts";

const COLORS = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#EC4899",
];

const DashboardAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [userStats, setUserStats] = useState<any>(null);
  const [overview, setOverview] = useState<any>(null);
  const [userAnalytics, setUserAnalytics] = useState<any>(null);
  const [courseStats, setCourseStats] = useState<any>(null);
  const [revenueStats, setRevenueStats] = useState<any>(null);
  const [contentStats, setContentStats] = useState<any>(null);
  const [recentUsers, setRecentUsers] = useState<any[]>([]);
  const [enrollmentTrends, setEnrollmentTrends] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [recheckBadgeLoading, setRecheckBadgeLoading] = useState(false);
  const [recheckBadgeResult, setRecheckBadgeResult] = useState<any>(null);
  const [eligibleUsers, setEligibleUsers] = useState<any[]>([]);
  const [showEligibleUsers, setShowEligibleUsers] = useState(false);
  const [eligibleBadgeError, setEligibleBadgeError] = useState<string | null>(
    null,
  );
  const [cleanupResult, setCleanupResult] = useState<any | null>(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupDryRun, setCleanupDryRun] = useState(true);
  const [activeChart, setActiveChart] = useState<"enrollments" | "revenue">(
    "enrollments",
  );

  const handleRecheckAllBadges = async () => {
    setRecheckBadgeLoading(true);
    setRecheckBadgeResult(null);
    try {
      const res = await gamificationApi.adminRecheckAllBadges();
      setRecheckBadgeResult(res);
    } catch (err: any) {
      const payload = err?.cause ?? err;
      console.error("Failed to recheck badges:", err, payload);
      setRecheckBadgeResult({
        error: err?.message || "Failed to recheck badges",
        details: payload,
      });
    } finally {
      setRecheckBadgeLoading(false);
    }
  };

  const handleRecheckUserBadges = async () => {
    if (!selectedUserId) return;
    setRecheckBadgeLoading(true);
    setRecheckBadgeResult(null);
    try {
      const res = await gamificationApi.adminRecheckUserBadges(selectedUserId);
      setRecheckBadgeResult(res);
    } catch (err: any) {
      const payload = err?.cause ?? err;
      console.error("Failed to recheck user badges:", err, payload);
      setRecheckBadgeResult({
        error: err?.message || "Failed to recheck user badges",
        details: payload,
      });
    } finally {
      setRecheckBadgeLoading(false);
    }
  };

  const handleShowEligibleUsers = async () => {
    setRecheckBadgeLoading(true);
    setRecheckBadgeResult(null);
    setEligibleBadgeError(null);
    try {
      const res = await gamificationApi.adminGetUsersEligibleForBadges();
      const list = (res as any)?.data ?? [];
      setEligibleUsers(list);
      setShowEligibleUsers(true);
    } catch (err: any) {
      const payload = err?.cause ?? err;
      const msg = err?.message || "Failed to load eligible users";
      setEligibleBadgeError(msg);
      setShowEligibleUsers(false);
      setRecheckBadgeResult({ error: msg, details: payload });
    } finally {
      setRecheckBadgeLoading(false);
    }
  };

  const handleCleanupOrphanedData = async () => {
    setCleanupLoading(true);
    setCleanupResult(null);
    try {
      const res = await adminApi.cleanupOrphanedUserData(cleanupDryRun);
      setCleanupResult(res);
    } catch (err: any) {
      console.error("Failed to cleanup orphaned data:", err);
      setCleanupResult({
        error: err?.message || "Failed to cleanup orphaned data",
      });
    } finally {
      setCleanupLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const results = await Promise.allSettled([
        adminApi.getStats(),
        analyticsApi.getOverview("30d"),
        analyticsApi.getUserStats("30d"),
        analyticsApi.getDashboard("30d"),
        analyticsApi.getRevenueStats("30d"),
        analyticsApi.getEnrollmentTrends("30d"),
        analyticsApi.getContentStats("30d"),
      ]);

      if (results[0].status === "fulfilled" && results[0].value.success) {
        setUserStats(results[0].value.data);
      }
      if (results[1].status === "fulfilled" && results[1].value.success) {
        setOverview(results[1].value.data);
      }
      if (results[2].status === "fulfilled" && results[2].value.success) {
        setUserAnalytics(results[2].value.data);
      }
      if (results[3].status === "fulfilled" && results[3].value.success) {
        setCourseStats(results[3].value.data);
      }
      if (results[4].status === "fulfilled" && results[4].value.success) {
        setRevenueStats(results[4].value.data);
      }
      if (results[5].status === "fulfilled") {
        const trendRes = results[5].value;
        const items = Array.isArray((trendRes as any)?.data)
          ? (trendRes as any).data
          : [];
        const mapped = items.map((item: any) => ({
          date: new Date(item._id).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          enrollments: item.enrollments,
          completed: item.completed,
          active: item.active,
        }));
        if (mapped.length) setEnrollmentTrends(mapped);
      }

      try {
        const usersRes = await adminApi.getUsers({
          limit: 5,
          sortBy: "createdAt",
          sortOrder: "desc",
        });
        if (usersRes.success) setRecentUsers(usersRes.data);
      } catch (usersErr) {
        console.error("Failed to fetch recent users:", usersErr);
      }
    } catch (err) {
      console.error("Failed to load dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  const roleDistributionData = userAnalytics?.roleDistribution?.map(
    (item: any) => ({
      name: item.role || "Unknown",
      value: item.count || 0,
    }),
  ) || [
    { name: "Students", value: userStats?.totalStudents || 5 },
    { name: "Instructors", value: userStats?.totalInstructors || 2 },
    { name: "Mentors", value: userStats?.totalMentors || 1 },
    { name: "Admins", value: userStats?.totalAdmins || 1 },
  ];

  const totalUsers =
    userStats?.totalUsers ?? overview?.overview?.totalUsers ?? 0;
  const totalCourses = overview?.overview?.totalCourses ?? 0;
  const totalRevenue =
    revenueStats?.totalRevenue ?? overview?.overview?.totalRevenue ?? 0;
  const activeUsers =
    overview?.overview?.activeEnrollments ??
    overview?.overview?.totalEnrollments ??
    userStats?.activeUsers ??
    0;
  const revenueGrowth =
    revenueStats?.revenueGrowth ?? overview?.growth?.revenue ?? 0;

  const stats = [
    {
      label: "Total Users",
      value: totalUsers.toLocaleString(),
      icon: Users,
      color: "bg-blue-500",
      bgLight: "bg-blue-50",
      textColor: "text-blue-600",
      change: overview?.growth?.users
        ? `${overview.growth.users > 0 ? "+" : ""}${overview.growth.users}%`
        : "+0%",
    },
    {
      label: "Total Courses",
      value: totalCourses.toLocaleString(),
      icon: BookOpen,
      color: "bg-green-500",
      bgLight: "bg-green-50",
      textColor: "text-green-600",
      change: overview?.growth?.courses
        ? `${overview.growth.courses > 0 ? "+" : ""}${overview.growth.courses}%`
        : "+0%",
    },
    {
      label: "Active Users",
      value: activeUsers.toLocaleString(),
      icon: Activity,
      color: "bg-orange-500",
      bgLight: "bg-orange-50",
      textColor: "text-orange-600",
      change: "+12%",
    },
    {
      label: "Total Revenue",
      value: `${(totalRevenue / 1000).toFixed(0)}k XAF`,
      icon: DollarSign,
      color: "bg-purple-500",
      bgLight: "bg-purple-50",
      textColor: "text-purple-600",
      change: revenueGrowth > 0 ? `+${revenueGrowth}%` : `${revenueGrowth}%`,
    },
  ];

  const enrollmentTrendData = enrollmentTrends.length
    ? enrollmentTrends
    : [
        { date: "Mon", enrollments: 0, completed: 0, active: 0 },
        { date: "Tue", enrollments: 0, completed: 0, active: 0 },
        { date: "Wed", enrollments: 0, completed: 0, active: 0 },
        { date: "Thu", enrollments: 0, completed: 0, active: 0 },
        { date: "Fri", enrollments: 0, completed: 0, active: 0 },
        { date: "Sat", enrollments: 0, completed: 0, active: 0 },
        { date: "Sun", enrollments: 0, completed: 0, active: 0 },
      ];

  const revenueData = revenueStats?.monthlyRevenue
    ?.slice(-6)
    .map((item: any) => ({
      month: item.month?.substring(0, 3) || item.month,
      revenue: item.revenue || 0,
    })) || [
    { month: "Jan", revenue: 0 },
    { month: "Feb", revenue: 0 },
    { month: "Mar", revenue: 0 },
    { month: "Apr", revenue: 0 },
    { month: "May", revenue: 0 },
    { month: "Jun", revenue: 0 },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <Loader className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg shadow-sm bg-gradient-to-r from-orange-600 to-orange-500">
                  <Shield className="w-4 h-4 text-white" />
                </div>
                <h1 className="text-lg font-bold text-gray-900 md:text-xl">
                  Admin Dashboard
                </h1>
              </div>
              <p className="text-[10px] text-gray-500 ml-10 md:text-xs">
                Platform overview and management
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all md:text-xs md:px-3 md:py-2">
                <Download className="w-3.5 h-3.5" />
                Export
              </button>
              <Link
                to="/dashboard/admin/users"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all md:text-xs md:px-3 md:py-2"
              >
                <UserPlus className="w-3.5 h-3.5" />
                Manage Users
              </Link>
              <Link
                to="/dashboard/admin/mentors"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] text-white rounded-lg bg-gradient-to-r from-purple-600 to-purple-500 hover:shadow-md transition-all md:text-xs md:px-3 md:py-2"
              >
                <UserCheck className="w-3.5 h-3.5" />
                Manage Mentors
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Grid - 4 columns responsive */}
        <div className="grid grid-cols-2 gap-3 mb-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl"
            >
              <div className="flex items-center justify-between mb-2">
                <div
                  className={`w-8 h-8 ${stat.bgLight} rounded-lg flex items-center justify-center`}
                >
                  <stat.icon className={`w-4 h-4 ${stat.textColor}`} />
                </div>
                <span className="text-sm font-bold text-gray-900">
                  {stat.value}
                </span>
              </div>
              <p className="text-[9px] text-gray-500 md:text-[10px]">
                {stat.label}
              </p>
              <p className="text-[8px] text-green-600 mt-1 md:text-[9px]">
                {stat.change} from last month
              </p>
            </div>
          ))}
        </div>

        {/* Gamification Backfill Section */}
        <div className="p-3 mb-5 bg-white border border-gray-100 shadow-sm rounded-xl md:p-4">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-4 h-4 text-orange-500" />
            <h2 className="text-[11px] font-bold text-gray-900 md:text-sm">
              Gamification Backfill
            </h2>
          </div>
          <p className="text-[10px] text-gray-500 mb-3">
            Recheck users for missing certificates/badges and award them if
            eligible.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleShowEligibleUsers}
              disabled={recheckBadgeLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium text-white rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 hover:shadow-md transition-all disabled:opacity-60"
            >
              {recheckBadgeLoading ? (
                <Loader className="w-3 h-3 animate-spin" />
              ) : (
                <Award className="w-3 h-3" />
              )}
              Show Eligible Users
            </button>
            <button
              onClick={handleRecheckAllBadges}
              disabled={recheckBadgeLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all disabled:opacity-60"
            >
              {recheckBadgeLoading ? (
                <Loader className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
              Recheck All Users
            </button>
            <input
              type="text"
              placeholder="User ID"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="px-2 py-1.5 text-[10px] border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 w-32"
            />
            <button
              onClick={handleRecheckUserBadges}
              disabled={recheckBadgeLoading || !selectedUserId}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all disabled:opacity-60"
            >
              {recheckBadgeLoading ? (
                <Loader className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
              Recheck User
            </button>
          </div>
          {recheckBadgeResult && (
            <div className="mt-3 p-2 rounded-lg bg-gray-50 border border-gray-100 text-[10px] text-gray-700 max-h-40 overflow-auto">
              <pre className="whitespace-pre-wrap">
                {JSON.stringify(recheckBadgeResult, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Charts Row 1 - Role Distribution & Chart Toggle */}
        <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-2 lg:gap-5">
          {/* User Role Distribution Pie Chart */}
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl md:p-4">
            <div className="flex items-center justify-between mb-2 md:mb-3">
              <h2 className="text-[11px] font-semibold text-gray-900 md:text-sm">
                User Role Distribution
              </h2>
              <div className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-[9px] text-gray-400">
                  {totalUsers} total
                </span>
              </div>
            </div>
            <div className="h-40 md:h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={roleDistributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={25}
                    outerRadius={45}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={false}
                    fontSize={10}
                  >
                    {roleDistributionData.map((entry: any, index: number) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 9 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Chart Toggle & Display */}
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl md:p-4">
            <div className="flex items-center justify-between mb-2 md:mb-3">
              <h2 className="text-[11px] font-semibold text-gray-900 md:text-sm">
                {activeChart === "enrollments"
                  ? "Enrollment Trends"
                  : "Revenue Trends"}
              </h2>
              <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setActiveChart("enrollments")}
                  className={`px-2 py-0.5 text-[9px] font-medium rounded-md transition-all ${
                    activeChart === "enrollments"
                      ? "bg-white text-orange-600 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Enrollments
                </button>
                <button
                  onClick={() => setActiveChart("revenue")}
                  className={`px-2 py-0.5 text-[9px] font-medium rounded-md transition-all ${
                    activeChart === "revenue"
                      ? "bg-white text-orange-600 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Revenue
                </button>
              </div>
            </div>
            <div className="h-40 md:h-48">
              <ResponsiveContainer width="100%" height="100%">
                {activeChart === "enrollments" ? (
                  <LineChart data={enrollmentTrendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 9 }}
                      stroke="#9ca3af"
                    />
                    <YAxis tick={{ fontSize: 9 }} stroke="#9ca3af" />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 9 }} />
                    <Line
                      type="monotone"
                      dataKey="enrollments"
                      stroke="#F59E0B"
                      strokeWidth={2}
                      dot={{ fill: "#F59E0B", strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 4 }}
                      name="New Enrollments"
                    />
                    <Line
                      type="monotone"
                      dataKey="completed"
                      stroke="#10B981"
                      strokeWidth={2}
                      dot={{ fill: "#10B981", strokeWidth: 2, r: 3 }}
                      name="Completed"
                    />
                  </LineChart>
                ) : (
                  <BarChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 9 }}
                      stroke="#9ca3af"
                    />
                    <YAxis
                      tick={{ fontSize: 9 }}
                      stroke="#9ca3af"
                      tickFormatter={(value) => `${value / 1000}k`}
                    />
                    <Tooltip
                      formatter={(value: number) => [
                        `${value.toLocaleString()} XAF`,
                        "Revenue",
                      ]}
                    />
                    <Bar
                      dataKey="revenue"
                      fill="#8B5CF6"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Charts Row 2 - Course Status & User Activity */}
        <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-2 lg:gap-5">
          {/* Course Status Distribution */}
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl md:p-4">
            <h2 className="text-[11px] font-semibold text-gray-900 mb-2 md:text-sm md:mb-3">
              Course Status
            </h2>
            <div className="h-40 md:h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      {
                        name: "Published",
                        value: overview?.overview?.publishedCourses || 0,
                      },
                      {
                        name: "Draft",
                        value: overview?.overview?.draftCourses || 0,
                      },
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={25}
                    outerRadius={45}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={false}
                  >
                    <Cell fill="#10B981" />
                    <Cell fill="#F59E0B" />
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 9 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* User Activity Stats */}
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl md:p-4">
            <h2 className="text-[11px] font-semibold text-gray-900 mb-2 md:text-sm md:mb-3">
              User Activity
            </h2>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 rounded-lg bg-blue-50">
                <div className="flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-blue-600" />
                  <span className="text-[11px] text-gray-700">Students</span>
                </div>
                <span className="text-sm font-bold text-blue-600">
                  {userStats?.totalStudents || 0}
                </span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-green-50">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-green-600" />
                  <span className="text-[11px] text-gray-700">Instructors</span>
                </div>
                <span className="text-sm font-bold text-green-600">
                  {userStats?.totalInstructors || 0}
                </span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-purple-50">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-purple-600" />
                  <span className="text-[11px] text-gray-700">Mentors</span>
                </div>
                <span className="text-sm font-bold text-purple-600">
                  {userStats?.totalMentors || 0}
                </span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-orange-50">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-orange-600" />
                  <span className="text-[11px] text-gray-700">
                    New Users (30d)
                  </span>
                </div>
                <span className="text-sm font-bold text-orange-600">
                  {userAnalytics?.newUsersLast30Days ||
                    userAnalytics?.overview?.newUsersLast30Days ||
                    overview?.overview?.newUsersLast30Days ||
                    0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Users Section */}
        <div className="p-3 mb-5 bg-white border border-gray-100 shadow-sm rounded-xl md:p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[11px] font-bold text-gray-900 md:text-sm">
              Recent Users
            </h2>
            <Link
              to="/dashboard/admin/users"
              className="text-[10px] text-orange-600 hover:underline"
            >
              View All
            </Link>
          </div>

          {/* Desktop Table View */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-2 text-gray-600 font-medium text-[11px]">
                    Name
                  </th>
                  <th className="text-left p-2 text-gray-600 font-medium text-[11px]">
                    Email
                  </th>
                  <th className="text-left p-2 text-gray-600 font-medium text-[11px]">
                    Role
                  </th>
                  <th className="text-left p-2 text-gray-600 font-medium text-[11px]">
                    Status
                  </th>
                  <th className="text-left p-2 text-gray-600 font-medium text-[11px]">
                    Joined
                  </th>
                </tr>
              </thead>
              <tbody>
                {recentUsers.map((user: any, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="p-2 font-medium text-gray-900 text-[11px]">
                      {user.firstName && user.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user.name || user.username || "N/A"}
                    </td>
                    <td className="p-2 text-gray-600 text-[11px]">
                      {user.email}
                    </td>
                    <td className="p-2 capitalize text-[11px]">{user.role}</td>
                    <td className="p-2">
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-[9px] ${
                          user.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {user.isActive ? "active" : "inactive"}
                      </span>
                    </td>
                    <td className="p-2 text-gray-500 text-[11px]">
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString()
                        : "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="space-y-2 md:hidden">
            {recentUsers.map((user: any, i) => (
              <div key={i} className="p-3 rounded-lg bg-gray-50">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-[11px] font-semibold text-gray-900">
                      {user.firstName && user.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user.name || user.username || "N/A"}
                    </p>
                    <p className="text-[9px] text-gray-500 mt-0.5">
                      {user.email}
                    </p>
                  </div>
                  <span
                    className={`px-1.5 py-0.5 rounded-full text-[8px] font-medium ${
                      user.isActive
                        ? "bg-green-100 text-green-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {user.isActive ? "active" : "inactive"}
                  </span>
                </div>
                <div className="flex items-center justify-between text-[9px] text-gray-500">
                  <span className="capitalize">Role: {user.role}</span>
                  <span>
                    Joined:{" "}
                    {user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString()
                      : "N/A"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Eligible Users Modal */}
        {showEligibleUsers && (
          <div className="p-3 mb-5 bg-white border border-gray-100 shadow-sm rounded-xl md:p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[11px] font-bold text-gray-900 md:text-sm">
                Users Eligible for Missing Badges ({eligibleUsers.length})
              </h2>
              <button
                onClick={() => setShowEligibleUsers(false)}
                className="text-[10px] text-gray-500 hover:text-gray-700"
              >
                Hide
              </button>
            </div>
            {eligibleUsers.length === 0 ? (
              <p className="text-[10px] text-gray-500">
                No users with missing eligible badges found.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2 text-gray-600 font-medium text-[11px]">
                        User
                      </th>
                      <th className="text-left p-2 text-gray-600 font-medium text-[11px]">
                        Missing Badges
                      </th>
                      <th className="text-left p-2 text-gray-600 font-medium text-[11px]">
                        Stats
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligibleUsers.map((user: any, i: number) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="p-2 text-gray-900 text-[11px]">
                          <div>
                            <p className="font-medium">{user.userName}</p>
                            <p className="text-[9px] text-gray-500">
                              {user.email}
                            </p>
                          </div>
                        </td>
                        <td className="p-2 text-[10px] text-gray-700">
                          <div className="flex flex-wrap gap-1">
                            {user.missingBadges?.map(
                              (badge: any, j: number) => (
                                <span
                                  key={j}
                                  className="px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100 text-[8px]"
                                >
                                  {badge.name}
                                </span>
                              ),
                            )}
                          </div>
                        </td>
                        <td className="p-2 text-[10px] text-gray-600">
                          <div>
                            <p>{user.stats?.totalPoints || 0} XP</p>
                            <p>{user.stats?.coursesCompleted || 0} courses</p>
                            <p>{user.stats?.lessonsWatched || 0} lessons</p>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Orphaned Data Cleanup */}
        <div className="p-3 mb-5 bg-white border border-gray-100 shadow-sm rounded-xl md:p-4">
          <div className="flex items-center gap-2 mb-3">
            <Database className="w-4 h-4 text-red-500" />
            <h2 className="text-[11px] font-bold text-gray-900 md:text-sm">
              Orphaned User Data Cleanup
            </h2>
          </div>
          <p className="text-[10px] text-gray-500 mb-3">
            Find and remove records that reference deleted users. Start with a
            dry-run to review what would be removed.
          </p>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <label className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] font-medium border border-gray-200 rounded-lg bg-white">
              <input
                type="checkbox"
                checked={cleanupDryRun}
                onChange={(e) => setCleanupDryRun(e.target.checked)}
                className="w-3.5 h-3.5 rounded"
              />
              Dry run
            </label>
            <button
              onClick={handleCleanupOrphanedData}
              disabled={cleanupLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium text-white rounded-lg bg-gradient-to-r from-red-600 to-red-500 hover:shadow-md transition-all disabled:opacity-60"
            >
              {cleanupLoading ? (
                <Loader className="w-3 h-3 animate-spin" />
              ) : (
                <Trash2 className="w-3 h-3" />
              )}
              {cleanupDryRun ? "Scan Orphaned Data" : "Delete Orphaned Data"}
            </button>
          </div>
          {cleanupResult && (
            <div className="p-2 rounded-lg bg-gray-50 border border-gray-100 text-[10px] text-gray-700 max-h-40 overflow-auto">
              <pre className="whitespace-pre-wrap">
                {JSON.stringify(cleanupResult, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Stats Summary Footer */}
        <div className="grid grid-cols-3 gap-3 mt-5">
          <div className="p-2 text-center bg-white border border-gray-100 shadow-sm rounded-xl">
            <div className="flex items-center justify-center w-6 h-6 mx-auto mb-0.5 rounded-lg bg-blue-100">
              <Users className="w-3 h-3 text-blue-600" />
            </div>
            <p className="text-[10px] font-bold text-gray-900">{totalUsers}</p>
            <p className="text-[7px] text-gray-500">Total Users</p>
          </div>
          <div className="p-2 text-center bg-white border border-gray-100 shadow-sm rounded-xl">
            <div className="flex items-center justify-center w-6 h-6 mx-auto mb-0.5 rounded-lg bg-green-100">
              <BookOpen className="w-3 h-3 text-green-600" />
            </div>
            <p className="text-[10px] font-bold text-gray-900">
              {totalCourses}
            </p>
            <p className="text-[7px] text-gray-500">Courses</p>
          </div>
          <div className="p-2 text-center bg-white border border-gray-100 shadow-sm rounded-xl">
            <div className="flex items-center justify-center w-6 h-6 mx-auto mb-0.5 rounded-lg bg-purple-100">
              <DollarSign className="w-3 h-3 text-purple-600" />
            </div>
            <p className="text-[10px] font-bold text-gray-900">
              {(totalRevenue / 1000).toFixed(0)}k
            </p>
            <p className="text-[7px] text-gray-500">Revenue (XAF)</p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default DashboardAdmin;
